<script lang="ts" setup>
import { computed, useSlots, type Component as VueComponent } from 'vue';

export type InsightCardVariant = 'success' | 'warning' | 'error' | 'info';

export interface InsightCardMetric {
  title: string;
  value: string | number;
}

export interface InsightCardKpiItem {
  label: string;
  value: string | number;
}

export interface InsightCardRecommendation {
  title: string;
  value: string;
}

interface Props {
  signal?: string;
  signalVariant?: InsightCardVariant;
  signalColor?: string;

  icon?: VueComponent;
  value?: string;
  valueDescription?: string;
  variant?: InsightCardVariant;
  tag?: string;
  tagVariant?: InsightCardVariant;

  title: string;
  subtitle?: string;

  progress?: number;
  progressVariant?: InsightCardVariant;
  detailLeft?: string;
  detailRight?: string;

  metrics?: InsightCardMetric[];
  items?: InsightCardKpiItem[];

  recommendation?: InsightCardRecommendation;

  selectable?: boolean;
  selected?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  signal: undefined,
  signalVariant: 'info',
  signalColor: undefined,
  icon: undefined,
  value: undefined,
  valueDescription: undefined,
  variant: undefined,
  tag: undefined,
  tagVariant: undefined,
  subtitle: undefined,
  progressVariant: undefined,
  detailLeft: undefined,
  detailRight: undefined,
  metrics: () => [],
  items: undefined,
  recommendation: undefined,
  selectable: false,
  selected: false,
});

const emit = defineEmits<{
  select: [];
}>();

const slots = useSlots();

const variantColorMap: Record<InsightCardVariant, { text: string; shade: string }> = {
  success: {
    text: 'var(--ui2-data-pine-forest)',
    shade: 'var(--ui2-shade-pine-forest-8)',
  },
  warning: {
    text: 'var(--ui2-primitive-desert-sunrise-500)',
    shade: 'var(--ui2-shade-desert-sunrise-8)',
  },
  error: {
    text: 'var(--ui2-primitive-ember-rose-500)',
    shade: 'var(--ui2-shade-ember-rose-8)',
  },
  info: {
    text: 'var(--ui2-data-pine-forest)',
    shade: 'var(--ui2-shade-pine-forest-8)',
  },
};

const signalActiveColor = computed(() => props.signalColor ?? variantColorMap[props.signalVariant].text);
const resolvedVariant = computed(() => props.variant ? variantColorMap[props.variant] : null);
const resolvedTagVariant = computed(() => variantColorMap[props.tagVariant ?? props.variant ?? 'success']);
const resolvedProgressVariant = computed(() => variantColorMap[props.progressVariant ?? props.variant ?? 'success']);

const progressWidth = computed(() => {
  const val = Math.max(0, Math.min(100, props.progress ?? 0));
  return `${val}%`;
});

const rootStyle = computed(() => {
  if (!props.selected) return undefined;
  if (props.signal != null) return { borderColor: signalActiveColor.value };
  if (resolvedVariant.value) return { borderColor: resolvedVariant.value.text };
  return { borderColor: 'var(--ui2-background-accent-primary, #226b6c)' };
});

const hasHeader = computed(
  () => props.value || props.valueDescription || props.tag || slots.icon || slots.action,
);

function handleClick() {
  if (props.selectable) {
    emit('select');
  }
}
</script>

<template>
  <component
    :is="selectable ? 'button' : 'div'"
    :type="selectable ? 'button' : undefined"
    :class="[
      $style.root,
      selectable && $style.selectable,
      selected && $style.selected,
    ]"
    :style="rootStyle"
    data-ui-component="UiInsightCard"
    @click="handleClick"
  >
    <!-- Signal badge (from SignalCard) -->
    <div v-if="signal" :class="[$style.signal, !signalColor && $style[`signal--${signalVariant}`]]">
      <span
        :class="$style.dot"
        :style="signalColor ? { backgroundColor: signalColor } : undefined"
      />
      <span
        :class="$style.signalText"
        :style="signalColor ? { color: signalColor } : undefined"
      >{{ signal }}</span>
    </div>

    <!-- Header row (from PerformanceCard + SegmentCard) -->
    <div v-if="hasHeader" :class="$style.header">
      <div :class="$style.headerLeft">
        <span v-if="slots.icon" :class="$style.headerIconSlot">
          <slot name="icon" />
        </span>
        <component
          v-else-if="icon"
          :is="icon"
          :class="$style.headerIcon"
          :style="resolvedVariant ? { color: resolvedVariant.text } : undefined"
          width="16"
          height="16"
          aria-hidden="true"
        />
        <div v-if="value || valueDescription" :class="$style.headerTextBlock">
          <span v-if="value" :class="$style.headerValue" :style="resolvedVariant ? { color: resolvedVariant.text } : undefined">{{ value }}</span>
          <span v-if="valueDescription" :class="$style.headerDescription">{{ valueDescription }}</span>
        </div>
        <span v-if="!value && !valueDescription && !icon && !slots.icon" :class="$style.headerTitle">{{ title }}</span>
      </div>
      <span v-if="tag" :class="$style.tag" :style="{ color: resolvedTagVariant.text, backgroundColor: resolvedTagVariant.shade }">{{ tag }}</span>
      <span v-if="slots.action" :class="$style.headerAction">
        <slot name="action" />
      </span>
    </div>

    <!-- Content details -->
    <div :class="$style.details">
      <div :class="$style.content">
        <div v-if="!hasHeader || value || valueDescription || icon || slots.icon" :class="[items ? $style.titleSegment : $style.title]">{{ title }}</div>
        <div v-if="subtitle" :class="$style.subtitle">{{ subtitle }}</div>
        <div v-if="progress != null" :class="$style.progressTrack">
          <div
            :class="$style.progressFill"
            :style="{ width: progressWidth, backgroundColor: resolvedProgressVariant.text }"
          />
        </div>
      </div>

      <div v-if="detailLeft || detailRight" :class="$style.detailsRow">
        <span v-if="detailLeft">{{ detailLeft }}</span>
        <span v-if="detailRight">{{ detailRight }}</span>
      </div>
    </div>

    <!-- Metrics cards (from SignalCard/PerformanceCard) -->
    <div v-if="metrics.length" :class="$style.metricsList">
      <div v-for="(metric, idx) in metrics" :key="idx" :class="$style.metricCard">
        <div :class="$style.metricTitle">{{ metric.title }}</div>
        <div :class="$style.metricValue">{{ metric.value }}</div>
      </div>
    </div>

    <!-- KPI items list (from SegmentCard) -->
    <div v-if="items?.length" :class="$style.itemsList">
      <div v-for="(item, index) in items" :key="index" :class="$style.itemRow">
        <span :class="$style.itemLabel">{{ item.label }}</span>
        <span :class="$style.itemValue">{{ item.value }}</span>
      </div>
    </div>

    <!-- Recommendation block (from SignalCard) -->
    <div v-if="recommendation" :class="$style.recommendation">
      <div :class="$style.recommendationTitle">{{ recommendation.title }}</div>
      <div :class="$style.recommendationValue">
        {{ recommendation.value }}
      </div>
    </div>
  </component>
</template>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/typography';

.root {
  @include shared.reset-button;

  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: var(--ui2-background-primary, #fff);
  border: 1px solid var(--ui2-stroke-secondary, #e4e5e8);
  border-radius: 12px;
  padding: 16px;
  width: 100%;
  text-align: left;
  cursor: default;
  font-family: var(--ui2-font-family, 'Poppins', sans-serif);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus-visible {
    outline: 2px solid var(--ui2-state-focus, #085cd9);
    outline-offset: -2px;
  }
}

.selectable {
  cursor: pointer;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.selected {
  border-width: 1.5px;
  padding: 15.5px;
}

/* Signal badge */

.signal {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}

.signalText {
  @include typography.style('data-xs-medium');

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.signal--success .dot { background-color: var(--ui2-data-pine-forest); }
.signal--success .signalText { color: var(--ui2-data-pine-forest); }
.signal--warning .dot { background-color: var(--ui2-primitive-desert-sunrise-500); }
.signal--warning .signalText { color: var(--ui2-primitive-desert-sunrise-500); }
.signal--error .dot { background-color: var(--ui2-primitive-ember-rose-500); }
.signal--error .signalText { color: var(--ui2-primitive-ember-rose-500); }
.signal--info .dot { background-color: var(--ui2-data-pine-forest); }
.signal--info .signalText { color: var(--ui2-data-pine-forest); }

/* Header row */

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.headerLeft {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1 1 0%;
}

.headerIconSlot {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.headerIcon {
  width: 16px;
  height: 16px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.headerTextBlock {
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.headerValue {
  @include typography.style('data-xs-medium');

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.headerDescription {
  @include typography.style('data-xs-medium');

  color: var(--ui2-content-tertiary, #978f91);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.headerTitle {
  @include typography.style('data-s-semibold');

  color: var(--ui2-content-primary, #010000);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.headerAction {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.tag {
  @include typography.style('data-xs-medium');

  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  border-radius: 4px;
  white-space: nowrap;
  flex: none;
}

/* Content details */

.details {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0 0;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.title {
  @include typography.style('heading-h4');

  color: var(--ui2-content-primary, #010000);
  word-break: break-word;
}

.titleSegment {
  @include typography.style('data-xs-semibold');

  color: var(--ui2-content-primary, #010000);
  word-break: break-word;
}

.subtitle {
  @include typography.style('data-xs-medium');

  color: var(--ui2-content-tertiary, #978f91);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Progress bar */

.progressTrack {
  width: 100%;
  height: 4px;
  border-radius: 8px;
  background: var(--ui2-shade-black-8, rgba(0, 0, 0, 0.08));
  overflow: hidden;
}

.progressFill {
  height: 100%;
  border-radius: 8px;
  transition: width 0.3s ease;
}

.detailsRow {
  @include typography.style('data-xs-medium');

  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--ui2-content-tertiary, #978f91);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Metrics cards */

.metricsList {
  display: flex;
  gap: 10px;
  padding-top: 16px;
}

.metricCard {
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 12px;
  border-radius: 8px;
  background: var(--ui2-background-tone-3, #fafafa);
}

.metricTitle {
  @include typography.style('data-xs-medium');

  color: var(--ui2-content-tertiary, #978f91);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metricValue {
  @include typography.style('data-xs-medium');

  color: var(--ui2-content-primary, #010000);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* KPI items list */

.itemsList {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.itemRow {
  @include typography.style('data-xs-regular');

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
}

.itemLabel {
  color: var(--ui2-content-secondary, #655e60);
}

.itemValue {
  @include typography.style('data-xs-medium');

  color: var(--ui2-content-primary, #010000);
}

/* Recommendation block */

.recommendation {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  justify-content: flex-start;
  min-height: 0;
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: var(--ui2-shade-blue-pulse-8, rgba(58, 98, 242, 0.08));
}

.recommendationTitle {
  @include typography.style('data-xs-medium');

  color: var(--ui2-content-primary, #010000);
}

.recommendationValue {
  @include typography.style('data-xs-regular');

  color: var(--ui2-content-primary, #010000);
  opacity: 0.5;
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
